import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * The primary action primitive. Variants map to DESIGN.md §5 button roles.
 * Feature code chooses a variant — it never reaches for a token directly.
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none font-sans font-medium outline-none transition-[background,border-color,transform,opacity,color,box-shadow] duration-150 ease-house focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // accent fill — the signature CTA (new chat, send). Mode-correct text
        // (white on cobalt in light, near-black on the lighter cobalt in dark).
        accent:
          "bg-[image:var(--btn-grad)] text-accent-ink shadow-[var(--btn-shadow),var(--btn-inset)] hover:-translate-y-px hover:shadow-[var(--btn-shadow-hover),var(--btn-inset)] active:translate-y-0 active:scale-[0.99]",
        // ink fill — strong neutral CTA.
        ink: "bg-[image:var(--btn-grad-ink)] text-paper shadow-[var(--btn-shadow),var(--btn-inset)] hover:-translate-y-px hover:shadow-[var(--btn-shadow-hover),var(--btn-inset)] active:translate-y-0 active:scale-[0.99]",
        // destructive fill — strong red + white text, reserved for irreversible
        // actions (delete, disconnect). Uses --danger-solid (not the washed
        // --danger, which is tuned for error text), never the accent.
        danger:
          "bg-danger-solid text-white hover:bg-[color-mix(in_srgb,var(--danger-solid),black_12%)] active:scale-[0.99]",
        // outlined ghost (Share, secondary)
        ghost:
          "bg-bubble text-ink hover:bg-tint",
        // quiet bordered CTA (New chat): soft accent-tinted border + accent text
        // (neutral in mono, coloured when an accent is set), transparent fill.
        outline:
          "bg-accent-subtle text-accent-text hover:bg-accent-edge active:scale-[0.99]",
        // borderless plate (list rows, tertiary)
        subtle: "bg-transparent text-ink hover:bg-line-soft",
      },
      size: {
        sm: "h-8 rounded-sm px-3 text-[13px]",
        md: "h-10 rounded-md px-4 text-sm",
        lg: "h-11 rounded-md px-5 text-[15px]",
      },
    },
    defaultVariants: { variant: "ink", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
